"use client";

import type { ParagraphData } from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { paragraphTranslations } from "./Paragraph.i18n";
import { Paragraph } from "./Paragraph";
import styles from "./Paragraph.module.css";

/**
 * ParagraphRenderer — system-wide reusable renderer dla listy akapitów.
 *
 * **Entry point dla każdego komponentu CMS** który chce renderować akapity
 * (`c_descriptive_page`, ale też przyszłe `c_blog`, panele admina z preview,
 * okna z rich content, itd.). Składa razem:
 *   - i18n context (PL/EN/DE per locale)
 *   - dispatcher `<Paragraph>` per layout
 *   - empty state gdy lista pusta
 *   - flex column container z gap'em
 *
 * **Reusable example:**
 *   ```tsx
 *   import { ParagraphRenderer } from "@/components/cms/Paragraphs/ParagraphRenderer";
 *
 *   <ParagraphRenderer paragraphs={content.paragraphs} />
 *   ```
 *
 * Komponent jest client-side ("use client") bo używa kontekstu locale i
 * niektóre layouty (Maps, Presentation, ContactForm) są interaktywne.
 */
export function ParagraphRenderer({
  paragraphs,
  className,
  emptyMessage,
}: ParagraphRendererProps) {
  const t = useT(paragraphTranslations);
  const list = Array.isArray(paragraphs) ? paragraphs : [];

  if (list.length === 0) {
    return (
      <div className={`${styles.root} ${className ?? ""}`}>
        <div className={styles.empty}>
          {emptyMessage ?? "—"}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.root} ${className ?? ""}`}>
      <div className={styles.list}>
        {list.map((paragraph) => (
          <Paragraph key={paragraph.id} paragraph={paragraph} t={t} />
        ))}
      </div>
    </div>
  );
}

export type ParagraphRendererProps = {
  paragraphs: ParagraphData[] | undefined;
  /** Dodatkowa klasa CSS na wrapper (np. dla większego gap'a w kontekście). */
  className?: string;
  /** Komunikat gdy lista pusta. Default: `"—"`. */
  emptyMessage?: string;
};
